<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BotSessionsAPI from 'dashboard/api/botSessions';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [String, Number], required: true },
});

const { t } = useI18n();

const session = ref(null);
const availableBot = ref(null);
const botAssigned = ref(false);
const isBusy = ref(false);

// Every conversation asks, and most have no bot at all, so a 404 or any other failure is simply
// "no bot here" rather than something to put in front of the agent.
const fetchSession = async () => {
  session.value = null;
  availableBot.value = null;
  botAssigned.value = false;
  try {
    const { data } = await BotSessionsAPI.show(props.conversationId);
    session.value = data.bot_session;
    availableBot.value = data.available_bot || null;
    botAssigned.value = Boolean(data.bot_assigned);
  } catch {
    session.value = null;
  }
};

watch(() => props.conversationId, fetchSession, { immediate: true });

const isPaused = computed(() => session.value?.status === 'paused');

// Where the conversation is in the bot's own funnel. Position included because "cobrança" alone
// does not say whether the customer is at the start or one step from the end.
const phase = computed(() => {
  const current = session.value?.current_phase;
  if (!current) return null;

  const phases = session.value.phases || [];
  const position = phases.indexOf(current) + 1;
  return { name: current, position, total: phases.length };
});

// Only worth showing once the conversation has actually changed hands.
const handoverTrail = computed(() => {
  const trail = session.value?.handover_trail || [];
  return trail.length > 1 ? trail.join(' → ') : '';
});

const statusLabel = computed(() => {
  if (!session.value) return '';
  const key = isPaused.value
    ? `PAUSED_REASON.${(session.value.paused_reason || 'manual').toUpperCase()}`
    : `STATUS.${session.value.status.toUpperCase()}`;
  return t(`CONVERSATION_SIDEBAR.BOT_SESSION.${key}`);
});

// Depois de uma transferência não sobra sessão para retomar, mas a inbox continua tendo um bot —
// e sem um gesto aqui o atendente não tem como devolver a conversa a ele.
const canHandback = computed(
  () => !session.value && Boolean(availableBot.value) && !botAssigned.value
);

// Devolvida e ainda sem sessão: o bot é o responsável outra vez e abre sessão na próxima mensagem
// do cliente. Dizer isso é o que diferencia "devolvi" de "não aconteceu nada".
const isWaitingForCustomer = computed(
  () => !session.value && Boolean(availableBot.value) && botAssigned.value
);

const handback = async () => {
  isBusy.value = true;
  try {
    const { data } = await BotSessionsAPI.handback(props.conversationId);
    session.value = data.bot_session;
    availableBot.value = data.available_bot || null;
    botAssigned.value = Boolean(data.bot_assigned);
  } catch (error) {
    useAlert(t('CONVERSATION_SIDEBAR.BOT_SESSION.ERROR'));
  } finally {
    isBusy.value = false;
  }
};

const toggle = async () => {
  isBusy.value = true;
  try {
    const { data } = isPaused.value
      ? await BotSessionsAPI.resume(props.conversationId)
      : await BotSessionsAPI.pause(props.conversationId);
    session.value = data.bot_session;
  } catch (error) {
    useAlert(t('CONVERSATION_SIDEBAR.BOT_SESSION.ERROR'));
  } finally {
    isBusy.value = false;
  }
};
</script>

<template>
  <div
    v-if="session || canHandback || isWaitingForCustomer"
    class="flex flex-col gap-1"
  >
    <span class="text-sm font-medium text-n-slate-12">
      {{ $t('CONVERSATION_SIDEBAR.BOT_SESSION.TITLE') }}
    </span>

    <div
      v-if="!session"
      class="flex flex-wrap items-center justify-between gap-2"
    >
      <div class="flex items-start flex-1 gap-2 min-w-0">
        <span class="mt-1.5 size-2 rounded-full shrink-0 bg-n-slate-9" />
        <span class="text-sm text-n-slate-11">
          {{ availableBot?.name }} ·
          {{
            isWaitingForCustomer
              ? $t('CONVERSATION_SIDEBAR.BOT_SESSION.WAITING_FOR_CUSTOMER')
              : $t('CONVERSATION_SIDEBAR.BOT_SESSION.OUT_OF_CONVERSATION')
          }}
        </span>
      </div>
      <NextButton
        v-if="canHandback"
        sm
        faded
        :disabled="isBusy"
        data-testid="bot-session-handback"
        :label="$t('CONVERSATION_SIDEBAR.BOT_SESSION.HANDBACK')"
        @click="handback"
      />
    </div>

    <div
      v-if="session"
      class="flex flex-wrap items-center justify-between gap-2"
    >
      <div class="flex items-start flex-1 gap-2 min-w-0">
        <span
          class="mt-1.5 size-2 rounded-full shrink-0"
          :class="isPaused ? 'bg-n-amber-9' : 'bg-n-teal-9'"
        />
        <span class="text-sm text-n-slate-11">
          {{ session.agent_bot?.name }} · {{ statusLabel }}
        </span>
      </div>
      <NextButton
        sm
        faded
        :disabled="isBusy"
        data-testid="bot-session-toggle"
        :label="
          isPaused
            ? $t('CONVERSATION_SIDEBAR.BOT_SESSION.RESUME')
            : $t('CONVERSATION_SIDEBAR.BOT_SESSION.PAUSE')
        "
        @click="toggle"
      />
    </div>

    <span v-if="phase" class="text-sm text-n-slate-11">
      {{
        phase.position
          ? $t('CONVERSATION_SIDEBAR.BOT_SESSION.PHASE_WITH_POSITION', {
              phase: phase.name,
              position: phase.position,
              total: phase.total,
            })
          : $t('CONVERSATION_SIDEBAR.BOT_SESSION.PHASE', { phase: phase.name })
      }}
    </span>

    <span v-if="handoverTrail" class="text-sm truncate text-n-slate-11">
      {{
        $t('CONVERSATION_SIDEBAR.BOT_SESSION.TRAIL', { trail: handoverTrail })
      }}
    </span>
  </div>
</template>
